import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  CreditCard as SubscriptionIcon,
  People as ClientsIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { superAdminLogout, getCurrentSuperAdmin } from '../services/superAdminAuthService';

// Drawer width when opened
const DRAWER_WIDTH = 240;

/**
 * Super Admin Layout Component
 * Includes header (AppBar) and sidebar (Drawer) for super admin pages
 * 
 * @param {Object} props Component props
 * @param {JSX.Element} props.children Child components to render in the main content area
 * @returns {JSX.Element} Super Admin Layout component
 */
const SuperAdminLayout = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const superAdmin = getCurrentSuperAdmin();
  
  // Check if screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State for drawer, user menu, and menu expansions
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [dashboardExpanded, setDashboardExpanded] = useState(false);
  const [clientsExpanded, setClientsExpanded] = useState(false);
  const [subscriptionsExpanded, setSubscriptionsExpanded] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  
  // Menu states
  const userMenuOpen = Boolean(userMenuAnchorEl);
  
  // Menu handlers
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };
  
  const handleLogout = () => {
    setUserMenuAnchorEl(null);
    superAdminLogout(navigate);
  };
  
  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };
  
  // Check if a menu item is active based on the current path
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  
  // Toggle submenu expansion
  const handleToggleDashboard = () => {
    setDashboardExpanded(!dashboardExpanded);
  };
  
  const handleToggleClients = () => {
    setClientsExpanded(!clientsExpanded);
  };
  
  const handleToggleSubscriptions = () => {
    setSubscriptionsExpanded(!subscriptionsExpanded);
  };
  
  const handleToggleSettings = () => {
    setSettingsExpanded(!settingsExpanded);
  };
  
  // Auto-expand subscriptions menu if on activation page
  useEffect(() => {
    if (location.pathname.includes('/activate-subscription')) {
      setSubscriptionsExpanded(true);
    }
  }, [location.pathname]);
  
  // Drawer content with navigation items
  const drawerContent = (
    <>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: [1],
          bgcolor: '#f5f7fa', // Professional light gray-blue
          color: '#2c3e50',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminIcon sx={{ color: '#5a9bd4' }} />
          <Typography variant="h6" noWrap component="div" sx={{ color: '#2c3e50', fontWeight: 600 }}>
            Super Admin
          </Typography>
        </Box>
        {isMobile && (
          <IconButton 
            onClick={handleDrawerToggle}
            sx={{ color: '#2c3e50' }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List component="nav">
        {/* Dashboard */}
        <ListItem disablePadding>
          <ListItemButton 
            selected={isActive('/superadmin/dashboard')} 
            onClick={() => handleNavigate('/superadmin/dashboard')}
            sx={{
              '&:hover': {
                backgroundColor: '#f0f7fa',
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f4f8',
                color: '#2c3e50',
                borderLeft: '3px solid #5a9bd4',
                '&:hover': {
                  backgroundColor: '#ddeef3',
                },
                '& .MuiListItemIcon-root': {
                  color: '#5a9bd4',
                },
              },
            }}
          >
            <ListItemIcon>
              <DashboardIcon sx={{ color: isActive('/superadmin/dashboard') ? '#5a9bd4' : '#6c757d' }} />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>
        
        {/* Clients */}
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleToggleClients}
            sx={{
              '&:hover': {
                backgroundColor: '#f0f7fa',
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f4f8',
                color: '#2c3e50',
                borderLeft: '3px solid #5a9bd4',
                '&:hover': {
                  backgroundColor: '#ddeef3',
                },
              },
            }}
          >
            <ListItemIcon>
              <ClientsIcon sx={{ color: isActive('/superadmin/clients') ? '#5a9bd4' : '#6c757d' }} />
            </ListItemIcon>
            <ListItemText primary="Clients" />
            {clientsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={clientsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ 
                pl: 4,
                '&:hover': {
                  backgroundColor: '#f0f7fa',
                },
                '&.Mui-selected': {
                  backgroundColor: '#e8f4f8',
                  color: '#2c3e50',
                  borderLeft: '3px solid #5a9bd4',
                  '&:hover': {
                    backgroundColor: '#ddeef3',
                  },
                },
              }}
              selected={isActive('/superadmin/clients')}
              onClick={() => handleNavigate('/superadmin/clients')}
            >
              <ListItemIcon>
                <BusinessIcon fontSize="small" sx={{ color: isActive('/superadmin/clients') ? '#5a9bd4' : '#6c757d' }} />
              </ListItemIcon>
              <ListItemText primary="All Clients" />
            </ListItemButton>
          </List>
        </Collapse>
        
        {/* Subscriptions */}
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleToggleSubscriptions}
            sx={{
              '&:hover': {
                backgroundColor: '#f0f7fa',
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f4f8',
                color: '#2c3e50',
                borderLeft: '3px solid #5a9bd4',
                '&:hover': {
                  backgroundColor: '#ddeef3',
                },
              },
            }}
          >
            <ListItemIcon>
              <SubscriptionIcon sx={{ color: isActive('/superadmin/subscriptions') ? '#5a9bd4' : '#6c757d' }} />
            </ListItemIcon>
            <ListItemText primary="Subscriptions" />
            {subscriptionsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={subscriptionsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ 
                pl: 4,
                '&:hover': {
                  backgroundColor: '#f0f7fa',
                },
                '&.Mui-selected': {
                  backgroundColor: '#e8f4f8',
                  color: '#2c3e50',
                  borderLeft: '3px solid #5a9bd4',
                  '&:hover': {
                    backgroundColor: '#ddeef3',
                  },
                },
              }}
              selected={isActive('/superadmin/subscriptions')}
              onClick={() => handleNavigate('/superadmin/subscriptions')}
            >
              <ListItemIcon>
                <SubscriptionIcon fontSize="small" sx={{ color: isActive('/superadmin/subscriptions') ? '#5a9bd4' : '#6c757d' }} />
              </ListItemIcon>
              <ListItemText primary="All Plans" />
            </ListItemButton>
            <ListItemButton 
              sx={{ 
                pl: 4,
                '&:hover': {
                  backgroundColor: '#f0f7fa',
                },
                '&.Mui-selected': {
                  backgroundColor: '#e8f4f8',
                  color: '#2c3e50',
                  borderLeft: '3px solid #5a9bd4',
                  '&:hover': {
                    backgroundColor: '#ddeef3',
                  },
                },
              }}
              selected={isActive('/superadmin/subscriptions/create')}
              onClick={() => handleNavigate('/superadmin/subscriptions/create')}
            >
              <ListItemIcon>
                <SubscriptionIcon fontSize="small" sx={{ color: isActive('/superadmin/subscriptions/create') ? '#5a9bd4' : '#6c757d' }} />
              </ListItemIcon>
              <ListItemText primary="Create Plan" />
            </ListItemButton>
            <ListItemButton 
              sx={{ 
                pl: 4,
                '&:hover': {
                  backgroundColor: '#f0f7fa',
                },
                '&.Mui-selected': {
                  backgroundColor: '#e8f4f8',
                  color: '#2c3e50',
                  borderLeft: '3px solid #5a9bd4',
                  '&:hover': {
                    backgroundColor: '#ddeef3',
                  },
                },
              }}
              selected={location.pathname.includes('/activate-subscription')}
              onClick={() => handleNavigate('/superadmin/clients')}
            >
              <ListItemIcon>
                <SubscriptionIcon fontSize="small" sx={{ color: location.pathname.includes('/activate-subscription') ? '#5a9bd4' : '#6c757d' }} />
              </ListItemIcon>
              <ListItemText primary="Activate Subscription" />
            </ListItemButton>
            <ListItemButton 
              sx={{ 
                pl: 4,
                '&:hover': {
                  backgroundColor: '#f0f7fa',
                },
                '&.Mui-selected': {
                  backgroundColor: '#e8f4f8',
                  color: '#2c3e50',
                  borderLeft: '3px solid #5a9bd4',
                  '&:hover': {
                    backgroundColor: '#ddeef3',
                  },
                },
              }}
              selected={isActive('/superadmin/subscriptions/addon-definitions')}
              onClick={() => handleNavigate('/superadmin/subscriptions/addon-definitions')}
            >
              <ListItemIcon>
                <SubscriptionIcon fontSize="small" sx={{ color: isActive('/superadmin/subscriptions/addon-definitions') ? '#5a9bd4' : '#6c757d' }} />
              </ListItemIcon>
              <ListItemText primary="Add-on Definitions" />
            </ListItemButton>
          </List>
        </Collapse>
        
        {/* Settings */}
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleToggleSettings}
            sx={{
              '&:hover': {
                backgroundColor: '#f0f7fa',
              },
              '&.Mui-selected': {
                backgroundColor: '#e8f4f8',
                color: '#2c3e50',
                borderLeft: '3px solid #5a9bd4',
                '&:hover': {
                  backgroundColor: '#ddeef3',
                },
              },
            }}
          >
            <ListItemIcon>
              <SettingsIcon sx={{ color: isActive('/superadmin/settings') ? '#5a9bd4' : '#6c757d' }} />
            </ListItemIcon>
            <ListItemText primary="Settings" />
            {settingsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ 
                pl: 4,
                '&:hover': {
                  backgroundColor: '#f0f7fa',
                },
                '&.Mui-selected': {
                  backgroundColor: '#e8f4f8',
                  color: '#2c3e50',
                  borderLeft: '3px solid #5a9bd4',
                  '&:hover': {
                    backgroundColor: '#ddeef3',
                  },
                },
              }}
              selected={isActive('/superadmin/settings')}
              onClick={() => handleNavigate('/superadmin/settings')}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" sx={{ color: isActive('/superadmin/settings') ? '#5a9bd4' : '#6c757d' }} />
              </ListItemIcon>
              <ListItemText primary="System Settings" />
            </ListItemButton>
          </List>
        </Collapse>
      </List>
    </>
  );
  
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { sm: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
          ml: { sm: drawerOpen ? `${DRAWER_WIDTH}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: '#e8f4f8', // Professional light blue-gray
          color: '#2c3e50', // Dark text for contrast
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2,
              display: { sm: 'none' },
              color: '#2c3e50',
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} /> {/* Spacer to push profile icon to the right */}
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* User Profile Menu */}
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleUserMenuOpen}
                size="small"
                sx={{ ml: 2 }}
                aria-controls={userMenuOpen ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={userMenuOpen ? 'true' : undefined}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#5a9bd4', color: '#fff' }}>
                  {superAdmin?.name?.charAt(0) || superAdmin?.username?.charAt(0) || 'A'}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* User Menu */}
          <Menu
            anchorEl={userMenuAnchorEl}
            id="account-menu"
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 1.5,
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleUserMenuClose} disabled sx={{ pointerEvents: 'none' }}>
              <Typography variant="subtitle2">{superAdmin?.name || superAdmin?.username || 'Super Admin'}</Typography>
            </MenuItem>
            <MenuItem onClick={handleUserMenuClose} disabled sx={{ pointerEvents: 'none' }}>
              <Typography variant="body2" color="text.secondary">{superAdmin?.email || ''}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              handleUserMenuClose();
              handleNavigate('/superadmin/profile');
            }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      {/* Side Navigation Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            zIndex: theme.zIndex.drawer,
            height: '100%',
            borderRight: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: '#ffffff', // Clean white background
          },
        }}
      >
        {drawerContent}
      </Drawer>
      
      {/* Main content area */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
          position: 'relative',
          transition: 'all 0.2s ease-in-out'
        }}
      >
        <Toolbar /> {/* Spacer to push content below app bar */}
        <Box sx={{ 
          flexGrow: 1, 
          p: 0,
          '& > *': {
            maxWidth: '100%',
            width: '100%'
          }
        }}> 
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default SuperAdminLayout;

